import { useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/outline";
import CooldownButton from "./CooldownButton";
import Crossfade from "./Crossfade";
import ThumbnailStrip from "./ThumbnailStrip";
import useSwipe from "./useSwipe";

const ImagePane = ({
  images,
  currentIndex,
  onPrevious,
  onNext,
  onThumbnailSelect,
  loading,
  darkMode,
  onSwipeUp,
  onSwipeDown,
  fullscreen,
  onToggleFullscreen,
}) => {
  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;
  const imageSrc = currentImage?.imgX || currentImage?.imgT;
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageSrc]);

  const crossfadeKey = imageError
    ? `${imageSrc ?? "empty"}-error`
    : (imageSrc ?? "empty");

  const swipeHandlers = useSwipe({
    onLeft: onNext,
    onRight: onPrevious,
    onUp: onSwipeUp,
    onDown: onSwipeDown,
  });

  return (
    <div
      className={`flex h-full flex-col ${darkMode ? "bg-neutral-900" : "bg-neutral-100"}`}
    >
      <div
        className="relative flex flex-1 select-none items-center justify-center overflow-hidden"
        {...swipeHandlers}
      >
        {!fullscreen && hasMultipleImages && (
          <CooldownButton
            onClick={onPrevious}
            className={`absolute left-3 z-10 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full shadow ${darkMode ? "bg-black/60 hover:bg-black/80" : "bg-white/80 hover:bg-white"}`}
          >
            <ChevronLeftIcon
              className={`h-5 w-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
            />
          </CooldownButton>
        )}
        <Crossfade transitionKey={crossfadeKey} className="h-full w-full">
          <div className="flex h-full w-full items-center justify-center">
            {imageSrc && !imageError ? (
              <img
                src={imageSrc}
                alt="Artwork"
                className={
                  fullscreen
                    ? "max-h-full max-w-full cursor-pointer object-contain"
                    : "max-h-full max-w-full object-contain"
                }
                onClick={fullscreen ? onToggleFullscreen : undefined}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className={darkMode ? "text-gray-500" : "text-gray-400"}>
                {loading ? "Loading…" : "No image available"}
              </div>
            )}
          </div>
        </Crossfade>
        {!fullscreen && hasMultipleImages && (
          <CooldownButton
            onClick={onNext}
            className={`absolute right-3 z-10 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full shadow ${darkMode ? "bg-black/60 hover:bg-black/80" : "bg-white/80 hover:bg-white"}`}
          >
            <ChevronRightIcon
              className={`h-5 w-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
            />
          </CooldownButton>
        )}
        {imageSrc && !imageError && (
          <button
            onClick={onToggleFullscreen}
            className={`absolute bottom-3 left-3 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full shadow ${darkMode ? "bg-black/60 hover:bg-black/80" : "bg-white/80 hover:bg-white"}`}
          >
            {fullscreen ? (
              <ArrowsPointingInIcon
                className={`h-5 w-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
              />
            ) : (
              <ArrowsPointingOutIcon
                className={`h-5 w-5 ${darkMode ? "text-gray-200" : "text-gray-700"}`}
              />
            )}
          </button>
        )}
        {!fullscreen && images.length > 0 && (
          <span
            className={`absolute bottom-3 right-3 text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {currentIndex + 1}/{images.length}
          </span>
        )}
      </div>
      {!fullscreen && hasMultipleImages && (
        <div
          className={`border-t px-4 ${darkMode ? "border-gray-700 bg-neutral-900" : "border-gray-200 bg-white"}`}
        >
          <ThumbnailStrip
            images={images}
            currentIndex={currentIndex}
            onSelect={onThumbnailSelect}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};

export default ImagePane;
